import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CustomDatePicker from './CustomDatePicker';
import MTRChatMini from './MTRChatMini';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Button,
  Switch,
  Collapse,
  Paper
} from '@mui/material';
import {
  SearchOutlined,
  FilterListOutlined,
  Replay as ReplayIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../theme/CustomThemeProvider';
import { createApiUrl } from '../utils/apiConfig';
import { getAuthHeaders } from '../utils/authUtils';
import { openInViewerWindow } from '../utils/viewerWindow';
import { formatDateWithAgeSync, loadFormatConfig } from '../utils/dateTimeFormat';

const MTRPopup = () => {
  const { t, i18n } = useTranslation();
  const { mode, toggleTheme } = useThemeContext();
  
  // State für Layout und UI
  const [orientation, setOrientation] = useState('portrait'); // Start im Hochformat
  
  // State für Daten
  const [currentUser, setCurrentUser] = useState(null);
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [previousExams, setPreviousExams] = useState([]);
  const [priorExamsModalityFilter, setPriorExamsModalityFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  
  // State für Filter und Suche
  const [filters, setFilters] = useState({
    patient: '', // Wird nicht gespeichert
    modality: '',
    status: 'all',
    referring_physician: '' // Neuer Filter für Zuweiser
  });
  
  // Separates State für Datumsbereich-Filter (wird NICHT gespeichert, da Default immer heute)
  const [dateFilterFrom, setDateFilterFrom] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD Format
  });
  const [dateFilterTo, setDateFilterTo] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD Format
  });

  // State für Filter-Dialog (Portrait-Modus) und Filter-Panel (Landscape-Modus)
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [landscapeFilterOpen, setLandscapeFilterOpen] = useState(false);

  // Prüfen, ob Filter aktiv sind (Standard-Werte für inaktive Filter)
  const hasActiveFilters = useMemo(() => {
    return filters.modality !== '' || 
           filters.status !== 'all' || 
           filters.referring_physician !== '';
  }, [filters]);

  // Gefilterte Prior Exams nach Modalität
  const filteredPreviousExams = useMemo(() => {
    if (priorExamsModalityFilter === 'all') {
      return previousExams;
    }
    return previousExams.filter(exam => 
      exam.modality && exam.modality.toLowerCase() === priorExamsModalityFilter.toLowerCase()
    );
  }, [previousExams, priorExamsModalityFilter]);

  // Verfügbare Modalitäten für Prior Exams Filter
  const availablePriorModalities = useMemo(() => {
    const modalities = [...new Set(previousExams
      .filter(exam => exam.modality)
      .map(exam => exam.modality)
    )];
    return modalities.sort();
  }, [previousExams]);

  // Popup-Fenster-Größe automatisch setzen
  useEffect(() => {
    const resizeWindow = () => {
      if (orientation === 'landscape') {
        // Querformat: 100% Breite, 300px Höhe
        window.resizeTo(window.screen.availWidth, 300);
        window.moveTo(0, 0);
      } else {
        // Hochformat: 300px Breite, 100% Höhe  
        window.resizeTo(300, window.screen.availHeight);
        window.moveTo(0, 0);
      }
    };

    // Beim Laden und bei Orientierungsänderung
    resizeWindow();
  }, [orientation]);

  // LAYOUTSTYLES ENTFERNT - VERWENDE DIREKTE NUCLEAR OPTION

  // User laden und Theme-Synchronisation
  useEffect(() => {
    const initializePopup = async () => {
      // Debug: Aktuelle Fenster-Position protokollieren
      const actualPos = {
        screenX: window.screenX,
        screenY: window.screenY,
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        screen: {
          width: window.screen.width,
          height: window.screen.height,
          availWidth: window.screen.availWidth,
          availHeight: window.screen.availHeight,
          availLeft: window.screen.availLeft || 0,
          availTop: window.screen.availTop || 0
        }
      };
      
      console.log('🪟 MTR POPUP: Fenster-Position beim Start:', actualPos);
      
      // Berechne wo das Fenster laut Orientierung sein sollte
      const expectedLandscape = {
        left: actualPos.screen.availLeft,
        top: actualPos.screen.availTop + actualPos.screen.availHeight - 300,
        width: actualPos.screen.availWidth,
        height: 300
      };
      
      const expectedPortrait = {
        left: actualPos.screen.availLeft,
        top: actualPos.screen.availTop,
        width: 300,
        height: actualPos.screen.availHeight
      };
      
      console.log('📐 ERWARTETE Positionen:', {
        landscape: expectedLandscape,
        portrait: expectedPortrait,
        actual: {
          left: actualPos.screenX,
          top: actualPos.screenY,
          width: actualPos.outerWidth,
          height: actualPos.outerHeight
        }
      });
      
      // Check: Stimmt aktuelle Position mit einer erwarteten Position überein?
      const isLandscapePosition = Math.abs(actualPos.screenY - expectedLandscape.top) < 50 && 
                                  Math.abs(actualPos.outerWidth - expectedLandscape.width) < 100;
      const isPortraitPosition = Math.abs(actualPos.screenX - expectedPortrait.left) < 50 && 
                                Math.abs(actualPos.outerWidth - expectedPortrait.width) < 100;
      
      console.log('✅ POSITIONS-CHECK:', {
        isLandscapePosition,
        isPortraitPosition,
        message: isLandscapePosition ? '🏞️ Fenster ist korrekt im Landscape (unten)' :
                 isPortraitPosition ? '📱 Fenster ist korrekt im Portrait (links)' :
                 '❌ Fenster ist an unerwarteter Position!'
      });
      
      // Force-Repositioning wird jetzt über separaten useEffect gehandhabt
      
      // Lade User-Daten über Backend (Keycloak-kompatibel)
      let user = null;
      try {
        const meResp = await fetch(createApiUrl('auth/me'), { headers: getAuthHeaders() });
        if (meResp.ok) {
          user = await meResp.json();
        }
      } catch (e) {
        console.warn('MTR Popup: Konnte User nicht laden', e);
      }
      setCurrentUser(user);
      
      // Theme-Synchronisation zwischen Fenstern über localStorage
      const syncTheme = () => {
        const storedTheme = localStorage.getItem('theme-mode');
        if (storedTheme && storedTheme !== mode) {
          // Wenn localStorage ein anderes Theme hat, update den Kontext
          // Note: Das funktioniert nur begrenzt in separaten Fenstern
          console.log(`Theme sync: ${storedTheme}`);
        }
      };

      // Initial sync
      syncTheme();
      
      // Lade Formatierungskonfiguration (für Datumsformatierung)
      console.log('MTR Popup: Lade Formatierungskonfiguration...');
      await loadFormatConfig();
      console.log('MTR Popup: Formatierungskonfiguration geladen');
      
      // WICHTIG: Lade frische task_settings aus der Datenbank für korrekte Orientierung
      if (user) {
        try {
          console.log('Popup: Lade frische task_settings für korrekte Orientierung...');
          const settingsResponse = await fetch(createApiUrl('profile/task-settings'), {
            headers: getAuthHeaders()
          });
          
          if (settingsResponse.ok) {
            const settingsData = await settingsResponse.json();
            console.log('Popup: Frische task_settings erhalten:', settingsData);
            
            // Parse die task_settings
            if (settingsData.task_settings) {
              try {
                const savedSettings = typeof settingsData.task_settings === 'string' 
                  ? JSON.parse(settingsData.task_settings) 
                  : settingsData.task_settings;
                console.log('Popup: Geladene Einstellungen:', savedSettings);
                
                if (savedSettings.mtr_filters) {
                  setFilters(prev => ({
                    ...prev,
                    modality: savedSettings.mtr_filters.modality || '',
                    status: savedSettings.mtr_filters.status || 'all',
                    referring_physician: savedSettings.mtr_filters.referring_physician || ''
                  }));
                }
                if (savedSettings.mtr_orientation) {
                  console.log('🔄 Popup: Setze Orientierung auf:', savedSettings.mtr_orientation);
                  console.log('🔄 Popup: Vorherige Orientierung war:', orientation);
                  setOrientation(savedSettings.mtr_orientation);
                  
                  // Sync mit localStorage für Konsistenz
                  localStorage.setItem('mtr-orientation', savedSettings.mtr_orientation);
                  console.log('🔄 Popup: Orientierung auch in localStorage gesetzt:', savedSettings.mtr_orientation);
                } else {
                  console.log('❓ Popup: Keine mtr_orientation in settings gefunden, verwende Default portrait');
                  setOrientation('portrait');
                  localStorage.setItem('mtr-orientation', 'portrait');
                }
              } catch (e) {
                console.warn('Fehler beim Parsen der MTR Einstellungen:', e);
              }
            }
          } else {
            console.warn('Popup: Konnte task_settings nicht laden:', settingsResponse.status);
          }
        } catch (error) {
          console.error('Popup: Fehler beim Laden der task_settings:', error);
        }
      }
    };
    
    initializePopup();
    
    // Listen auf storage events (funktioniert zwischen Fenstern)
    const handleStorageChange = (e) => {
      if (e.key === 'theme-mode' && e.newValue !== mode) {
        // Theme hat sich geändert - Page neu laden
        window.location.reload();
      } else if (e.key === 'mtr-orientation-change' && e.newValue) {
        // Orientierungsänderung vom Dashboard - Fenster schließen
        try {
          const orientationData = JSON.parse(e.newValue);
          console.log('Orientierungsänderung erkannt:', orientationData);
          // Fenster schließen, damit ein neues mit der richtigen Orientierung geöffnet wird
          window.close();
        } catch (err) {
          console.warn('Fehler beim Verarbeiten der Orientierungsänderung:', err);
        }
      } else if (e.key === 'mtr-logout-signal' && e.newValue) {
        // Logout-Signal vom Hauptfenster - Popup schließen
        try {
          const logoutData = JSON.parse(e.newValue);
          console.log('📨 MTR POPUP: Logout-Signal empfangen:', logoutData);
          console.log('🚪 MTR POPUP: Schließe Popup aufgrund von Logout...');
          window.close();
        } catch (err) {
          console.warn('Fehler beim Verarbeiten des Logout-Signals:', err);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [mode]);

  // (Debug-Code entfernt - Filter-Status funktioniert jetzt korrekt)

  // Exams laden
  const fetchExams = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const response = await fetch(
        createApiUrl(`new-exams/user/${currentUser.id}`), 
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log(`MTR Popup: ${data.length} Untersuchungen geladen (ALLE ohne Tenant-Filter)`);
        setExams(data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Untersuchungen:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      fetchExams();
    }
  }, [currentUser, fetchExams]);

  // Force-Repositioning nach Orientierungsänderung
  useEffect(() => {
    if (!orientation) return; // Warte bis Orientierung geladen ist
    
    // Kurze Verzögerung für Theme/UI-Update
    const timer = setTimeout(() => {
      console.log('🔧 FORCE-REPOSITION: Prüfe Fenster-Position für Orientierung:', orientation);
      
      const actualPos = {
        screenX: window.screenX,
        screenY: window.screenY,
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
        screen: {
          width: window.screen.width,
          height: window.screen.height,
          availWidth: window.screen.availWidth,
          availHeight: window.screen.availHeight,
          availLeft: window.screen.availLeft || 0,
          availTop: window.screen.availTop || 0
        }
      };
      
      const expectedLandscape = {
        left: actualPos.screen.availLeft,
        top: actualPos.screen.availTop + actualPos.screen.availHeight - 300,
        width: actualPos.screen.availWidth,
        height: 300
      };
      
      const expectedPortrait = {
        left: actualPos.screen.availLeft,
        top: actualPos.screen.availTop,
        width: 300,
        height: actualPos.screen.availHeight
      };
      
      // Position-Checks
      const isLandscapePosition = Math.abs(actualPos.screenY - expectedLandscape.top) < 50 && 
                                  Math.abs(actualPos.outerWidth - expectedLandscape.width) < 100;
      const isPortraitPosition = Math.abs(actualPos.screenX - expectedPortrait.left) < 50 && 
                                Math.abs(actualPos.outerWidth - expectedPortrait.width) < 100;
      
      console.log('🔧 FORCE-REPOSITION Check:', {
        orientation,
        isLandscapePosition,
        isPortraitPosition,
        actualPos: {x: actualPos.screenX, y: actualPos.screenY, w: actualPos.outerWidth, h: actualPos.outerHeight}
      });
      
      // Repositioning falls nötig
      if (orientation === 'landscape' && !isLandscapePosition) {
        console.log('⚡ LANDSCAPE KORREKTUR: Bewege Fenster nach unten');
        try {
          window.moveTo(expectedLandscape.left, expectedLandscape.top);
          window.resizeTo(expectedLandscape.width, expectedLandscape.height);
          console.log('✅ LANDSCAPE KORREKTUR erfolgreich');
        } catch (error) {
          console.warn('❌ Landscape Repositionierung fehlgeschlagen:', error);
        }
      } else if (orientation === 'portrait' && !isPortraitPosition) {
        console.log('⚡ PORTRAIT KORREKTUR: Bewege Fenster nach links');
        try {
          window.moveTo(expectedPortrait.left, expectedPortrait.top);
          window.resizeTo(expectedPortrait.width, expectedPortrait.height);
          console.log('✅ PORTRAIT KORREKTUR erfolgreich');
        } catch (error) {
          console.warn('❌ Portrait Repositionierung fehlgeschlagen:', error);
        }
      } else {
        console.log('✅ Fenster ist bereits korrekt positioniert für:', orientation);
      }
    }, 800); // Längere Verzögerung für vollständige Initialisierung
    
    return () => clearTimeout(timer);
  }, [orientation]); // Reagiert auf Orientierungs-Änderungen

  // Voruntersuchungen laden
  const fetchPreviousExams = useCallback(async (patientId) => {
    if (!patientId) {
      setPreviousExams([]);
      return;
    }
    
    try {
      setLoadingPrevious(true);
      const response = await fetch(
        createApiUrl(`prior-studies/?patient_id=${patientId}`), 
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        const filteredData = data.filter(study => study.patient_id === patientId);
        setPreviousExams(filteredData);
      } else {
        setPreviousExams([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Voruntersuchungen:', error);
      setPreviousExams([]);
    } finally {
      setLoadingPrevious(false);
    }
  }, []);

  // Bei Auswahl einer Untersuchung Voruntersuchungen laden (mit Toggle)
  const handleExamSelect = (exam) => {
    // Toggle-Verhalten: Wenn gleicher Patient geklickt wird, deselektieren
    if (selectedExam?.id === exam.id) {
      setSelectedExam(null);
      setPreviousExams([]); // Voruntersuchungen leeren
      console.log('MTR Popup: Patient deselektiert, Voruntersuchungen geleert');
    } else {
      setSelectedExam(exam);
      if (exam?.patient_id) {
        console.log('MTR Popup: Neuer Patient ausgewählt:', exam.patient_name);
        fetchPreviousExams(exam.patient_id);
      }
    }
  };

  // Viewer-Einstellungen laden
  const [viewerConfig, setViewerConfig] = useState({
    web: { base_url: '', test_study_uid: '' },
    client: { base_url: '', test_study_uid: '' }
  });

  useEffect(() => {
    // Viewer-Konfiguration laden (für alle Benutzer über profile/viewer-config)
    const fetchViewerConfig = async () => {
      try {
        const response = await fetch(createApiUrl('profile/viewer-config'), {
          headers: getAuthHeaders()
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.viewer_config) {
            setViewerConfig({
              web: {
                base_url: data.viewer_config.web.base_url || '',
                test_study_uid: data.viewer_config.web.test_study_uid || ''
              },
              client: {
                base_url: data.viewer_config.client.base_url || '',
                test_study_uid: data.viewer_config.client.test_study_uid || ''
              }
            });
          }
        } else {
          console.warn('Fehler beim Laden der Viewer-Konfiguration:', response.status);
        }
      } catch (error) {
        console.error('Fehler beim Laden der Viewer-Konfiguration:', error);
      }
    };

    if (currentUser) {
      fetchViewerConfig();
    }
  }, [currentUser]);

  // Viewer für Voruntersuchung öffnen (vollständige Logik aus ApprovalWorkflow)
  const handleStudyOpen = (study) => {
    if (!study.study_instance_uid) {
      alert(t('approval.error.noStudyUID', 'Keine Study Instance UID gefunden.'));
      return;
    }

    if (!currentUser || !currentUser.viewer_preference) {
      alert(t('approval.error.viewerSettingUnavailable', 'Viewer-Einstellungen sind nicht verfügbar.'));
      return;
    }

    // Spezielle Modalitäten immer mit Webviewer öffnen
    const studyModality = study.modality ? study.modality.toUpperCase() : '';
    
    let userViewerType;
    let forceWebViewer = false;
    
    // ECG/EKG: Prüfe ob Modalität ECG oder EKG ENTHÄLT (z.B. ECG/SR, ECG\SR, EKG-Monitor)
    if (studyModality.includes('ECG') || studyModality.includes('EKG')) {
      forceWebViewer = true;
    }
    // OT/DOC: Exakte Übereinstimmung
    else if (studyModality === 'OT' || studyModality === 'DOC') {
      forceWebViewer = true;
    }
    
    if (forceWebViewer) {
      userViewerType = 'web'; // Forciere Webviewer für spezielle Modalitäten
    } else {
      userViewerType = currentUser.viewer_preference; // Verwende User-Präferenz für andere
    }
    
    const viewerSetting = viewerConfig[userViewerType];

    if (!viewerSetting) {
      // Spezielle Fehlermeldung für erzwungene Webviewer-Modalitäten
      if (forceWebViewer) {
        alert(t('approval.error.webViewerNotConfigured', { 
          modality: studyModality 
        }) || `Webviewer für Modalität "${studyModality}" ist nicht konfiguriert. Bitte konfigurieren Sie den Webviewer in der Serververwaltung.`);
      } else {
        alert(t('approval.error.viewerConfigNotFound', { 
          viewerType: userViewerType, 
          available: Object.keys(viewerConfig).join(', ') 
        }, `Viewer-Konfiguration für "${userViewerType}" nicht gefunden.`));
      }
      return;
    }

    let baseUrl = viewerSetting.base_url;

    if (!baseUrl) {
      alert(t('approval.error.noViewerURL', { viewerType: userViewerType }, `Keine Viewer-URL für "${userViewerType}" konfiguriert.`));
      return;
    }

    // Ersetze den Platzhalter durch die tatsächliche Study Instance UID
    const viewerUrl = baseUrl.replace('[studyInstanceUID]', study.study_instance_uid);

    try {
      if (userViewerType === 'web') {
        openInViewerWindow(viewerUrl);
        console.log('MTR Popup: Web-Viewer geöffnet für Studie:', study.study_instance_uid);
      } else if (userViewerType === 'client') {
        // Starte Client-Anwendung (Custom Protocol)
        window.location.href = viewerUrl;
        console.log('MTR Popup: Client-Viewer geöffnet für Studie:', study.study_instance_uid);
      }
    } catch (error) {
      console.error('MTR Popup: Fehler beim Öffnen des Viewers:', error);
      alert(t('approval.error.viewerOpen', 'Fehler beim Öffnen des Viewers.'));
    }
  };

  // Filter speichern (nur persistente Filter, nicht patient)
  const saveFilters = useCallback(async (newFilters, newOrientation = null) => {
    if (!currentUser) {
      console.warn('currentUser not available yet, cannot save filters');
      return;
    }
    
    try {
      let currentSettings = {};
      try {
        currentSettings = currentUser.task_settings ? 
          JSON.parse(currentUser.task_settings) : {};
      } catch (parseError) {
        console.warn('Fehler beim Parsen der task_settings, verwende leere Einstellungen:', parseError);
        currentSettings = {};
      }
      
      // Nur persistente Filter speichern (patient wird ausgeschlossen)
      const persistentFilters = {
        modality: newFilters.modality,
        status: newFilters.status,
        referring_physician: newFilters.referring_physician
      };
      
      const updatedSettings = {
        ...currentSettings,
        mtr_filters: persistentFilters,
        // Immer die aktuelle Orientierung beibehalten (oder die neue, falls übergeben)
        mtr_orientation: newOrientation || orientation
      };
      
      console.log('Filter-Speicherung - currentSettings:', currentSettings);
      console.log('Filter-Speicherung - updatedSettings:', updatedSettings);
      
      // Verwende die richtige API
      const response = await fetch(createApiUrl('profile/task-settings'), {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          task_settings: JSON.stringify(updatedSettings)
        })
      });
      
      if (response.ok) {
        // User-Daten aktualisieren
        const updatedUser = { ...currentUser, task_settings: JSON.stringify(updatedSettings) };
        setCurrentUser(updatedUser);
        console.log('Filter erfolgreich gespeichert:', persistentFilters);
      } else {
        console.error('Fehler beim Speichern der Filter:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Fehler beim Speichern der Filter:', error);
    }
  }, [currentUser]);

  // Filter ändern und automatisch speichern (nur für persistente Filter)
  const handleFilterChange = (filterKey, value) => {
    const newFilters = { ...filters, [filterKey]: value };
    setFilters(newFilters);
    
    // Nur persistente Filter speichern
    if (['modality', 'status', 'referring_physician'].includes(filterKey)) {
      saveFilters(newFilters);
    }
  };

  // Orientierung wechseln - nicht mehr verwendet, aber für später verfügbar
  // const handleOrientationToggle = () => {
  //   const newOrientation = orientation === 'landscape' ? 'portrait' : 'landscape';
  //   setOrientation(newOrientation);
  //   saveFilters(filters, newOrientation);
  // };

  // Status-Kreis Farben (mit offiziellen Farben aus style.css)
  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED': return 'var(--rd-success)';
      case 'REJECTED': return 'var(--rd-error)';
      case 'NEW': 
      default: return 'var(--rd-text-disabled)';
    }
  };

  // Datumsformatierung (exakt wie im ApprovalWorkflow)
  // Lokale internationalisierte Version der formatDateWithAge Funktion
  const formatDateWithAge = (dateString) => {
    if (!dateString) return '-';
    
    const birthDate = new Date(dateString);
    if (isNaN(birthDate)) return '-';
    
    const today = new Date();
    
    // Alter berechnen
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    // Datum formatieren basierend auf der aktuellen Sprache
    const locale = i18n.language === 'de' ? 'de-DE' : 
                   i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    const formattedDate = birthDate.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
    
    return `${formattedDate} (${age} ${t('common.years', 'Jahre')})`;
  };

  // Filter funktionen
  const filteredExams = useMemo(() => {
    return exams.filter(exam => {
      // Datumsbereich-Filter (observation_date)
      if (dateFilterFrom || dateFilterTo) {
        const examDate = exam.observation_date ? new Date(exam.observation_date).toISOString().split('T')[0] : null;
        if (examDate) {
          if (dateFilterFrom && examDate < dateFilterFrom) {
            return false;
          }
          if (dateFilterTo && examDate > dateFilterTo) {
            return false;
          }
        } else {
          // Exams ohne Datum ausschließen wenn ein Datumsfilter gesetzt ist
          return false;
        }
      }
      
      // Freitextsuche in Patientenname UND Studienbeschreibung
      if (filters.patient) {
        const searchText = filters.patient.toLowerCase();
        const patientMatch = exam.patient_name?.toLowerCase().includes(searchText);
        const studyMatch = exam.study_description?.toLowerCase().includes(searchText);
        if (!patientMatch && !studyMatch) {
          return false;
        }
      }
      if (filters.modality && filters.modality !== 'all' && exam.modality !== filters.modality) {
        return false;
      }
      if (filters.status !== 'all' && exam.status !== filters.status) {
        return false;
      }
      if (filters.referring_physician && !exam.referring_physician?.toLowerCase().includes(filters.referring_physician.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [exams, filters, dateFilterFrom, dateFilterTo]);

  // Verfügbare Modalitäten und Zuweiser für Filter
  const availableModalities = useMemo(() => {
    const modalities = [...new Set(exams.map(exam => exam.modality).filter(Boolean))];
    return modalities.sort();
  }, [exams]);

  const availableReferringPhysicians = useMemo(() => {
    const physicians = [...new Set(exams.map(exam => exam.referring_physician).filter(Boolean))];
    return physicians.sort();
  }, [exams]);

  // =================== NUCLEAR OPTION: SCROLLBARS KILLER ===================
  useEffect(() => {
    // GLOBAL ALLE SCROLLBARS DEAKTIVIEREN
    const style = document.createElement('style');
    style.textContent = `
      * { 
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }
      *::-webkit-scrollbar { 
        display: none !important; 
        width: 0 !important;
        height: 0 !important;
      }
      body, html {
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        max-width: 100vw !important;
        max-height: 100vh !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // =================== KEYBOARD SHORTCUTS ===================
  useEffect(() => {
    const handleKeyDown = (e) => {
      // SHIFT + S = Suche öffnen
      if (e.shiftKey && e.key.toLowerCase() === 's') {
        e.preventDefault();
        console.log('MTR Cockpit: SHIFT + S gedrückt - Öffne Suche im Dashboard');
        
        // Navigiere im Hauptfenster (window.opener)
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.location.href = window.opener.location.origin + '/search';
            window.opener.focus();
          } catch (error) {
            console.error('Fehler beim Öffnen der Suche:', error);
          }
        } else {
          console.warn('Hauptfenster nicht verfügbar');
        }
      }
      
      // SHIFT + P = Patient hinzufügen öffnen
      if (e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        console.log('MTR Cockpit: SHIFT + P gedrückt - Öffne Patient hinzufügen im Dashboard');
        
        // Navigiere im Hauptfenster (window.opener)
        if (window.opener && !window.opener.closed) {
          try {
            window.opener.location.href = window.opener.location.origin + '/add-patient';
            window.opener.focus();
          } catch (error) {
            console.error('Fehler beim Öffnen von Patient hinzufügen:', error);
          }
        } else {
          console.warn('Hauptfenster nicht verfügbar');
        }
      }
    };
    
    // Event Listener hinzufügen
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // =================== AUTO-REFRESH SYSTEM ===================
  useEffect(() => {
    if (!currentUser) return;
    
    let lastActivityRef = Date.now(); // Verwende lokale Ref statt State
    
    // Activity tracking - Update lastActivity bei User-Interaktionen
    const updateLastActivity = () => {
      lastActivityRef = Date.now();
    };
    
    // Activity event listeners
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    activityEvents.forEach(event => {
      window.addEventListener(event, updateLastActivity, true);
    });
    
    // Timer für Auto-Refresh (prüft alle 60 Sekunden)
    const refreshInterval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = (now - lastActivityRef) / 1000; // in Sekunden
      
      // Nur refreshen wenn User mindestens 10 Sekunden inaktiv war
      if (timeSinceLastActivity >= 10) {
        // Stiller Refresh ohne Loading-Indicator oder State-Checks
        fetch(createApiUrl(`new-exams/user/${currentUser.id}`), { 
          headers: getAuthHeaders() 
        })
        .then(response => response.ok ? response.json() : Promise.reject('Failed to fetch'))
        .then(data => {
          setExams(data);
        })
        .catch(error => {
          // Silent fail - keine Logs für normale Netzwerkfehler
        });
      }
    }, 60000); // Alle 60 Sekunden prüfen
    
    return () => {
      // Cleanup
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateLastActivity, true);
      });
      clearInterval(refreshInterval);
    };
  }, [currentUser]); // Nur currentUser als Dependency, nicht lastActivity oder loading

  return (
    <Box sx={{
      // ABSOLUTE NUCLEAR CONTAINER
      position: 'fixed',
      top: 0, left: 0, right: 0, bottom: 0,
      width: '100vw', height: '100vh',
      overflow: 'hidden !important',
      backgroundColor: 'var(--rd-bg-default)',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      margin: 0, padding: 0
    }}>
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: orientation === 'landscape' ? 'row' : 'column',
        overflow: 'hidden !important',
        width: '100%', height: '100%',
        minHeight: 0, minWidth: 0,
        boxSizing: 'border-box',
        margin: 0, padding: 1
      }}>
        {orientation === 'landscape' ? (
          // Querformat: Filter-Button + 3 Spalten
          <>
            {/* Vertikaler Filter-Button ganz links */}
            <Box sx={{
              width: '32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              borderRight: `1px solid ${'var(--rd-divider)'}`,
              backgroundColor: 'var(--rd-bg-subtle)'
            }}>
              {/* Aktualisieren Button - quadratisch, nur Icon */}
              <Button
                size="small"
                variant="outlined"
                onClick={fetchExams}
                sx={{
                  minWidth: '28px',
                  width: '28px',
                  height: '28px',
                  p: 0,
                  mb: 0.5,
                  mt: 0.5,
                  borderColor: 'var(--rd-primary)',
                  color: 'var(--rd-primary)',
                  '&:hover': {
                    backgroundColor: 'color-mix(in srgb, var(--rd-primary) 10%, transparent)',
                    borderColor: 'var(--rd-primary-hover)'
                  }
                }}
              >
                <ReplayIcon sx={{ fontSize: '1rem' }} />
              </Button>
              
              {/* Filter Button */}
              <Button
                size="small"
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                onClick={() => setLandscapeFilterOpen(!landscapeFilterOpen)}
                sx={{
                  minWidth: '28px',
                  width: '28px',
                  height: 'calc(100% - 36px)',
                  p: 0,
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  fontSize: '0.7rem',
                  transform: 'rotate(180deg)',
                  backgroundColor: hasActiveFilters ? 'var(--rd-primary)' : 'transparent',
                  borderColor: 'var(--rd-primary)',
                  color: hasActiveFilters ? 'var(--rd-primary-contrast)' : 'var(--rd-primary)',
                  '&:hover': {
                    backgroundColor: hasActiveFilters ? 'var(--rd-primary-hover)' : 'color-mix(in srgb, var(--rd-primary) 10%, transparent)',
                    borderColor: 'var(--rd-primary-hover)'
                  },
                  '& .MuiButton-startIcon': {
                    transform: 'rotate(90deg)',
                    margin: 0
                  }
                }}
                startIcon={<FilterListOutlined fontSize="small" />}
              >
                {hasActiveFilters ? `${t('mtr.filter.title', 'Filter')} (${t('mtr.filter.active', 'aktiv')})` : t('mtr.filter.title', 'Filter')}
              </Button>
            </Box>
            
            {/* Filter-Panel (Slide-Out) - Push Effect */}
            <Box sx={{
              width: landscapeFilterOpen ? '600px' : '0px',
              height: '100%',
              backgroundColor: 'var(--rd-bg-paper)',
              borderRight: landscapeFilterOpen ? `1px solid ${'var(--rd-border)'}` : 'none',
              transition: 'width 0.3s ease',
              overflow: 'hidden',
              flexShrink: 0
            }}>
              <Box sx={{ 
                width: '600px', 
                height: '100%', 
                p: 2, 
                overflow: 'hidden',
                opacity: landscapeFilterOpen ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'var(--rd-primary)', fontWeight: 'bold', fontSize: '0.9rem' }}>
                {t('mtr.filter.title', 'Filter')}
              </Typography>
              
              {/* 2-spaltiges Filter-Layout - Landscape Fixed Position */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                {/* Linke Spalte: Nur Datum */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* Datumsbereich Toggle Switch */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {t('mtr.filter.allDates', 'Alle Daten')}
                    </Typography>
                    <Switch
                      size="small"
                      checked={!(dateFilterFrom === '' && dateFilterTo === '')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Aktiviere Datumsfilter
                          const today = new Date().toISOString().split('T')[0];
                          setDateFilterFrom(today);
                          setDateFilterTo(today);
                        } else {
                          // Deaktiviere Datumsfilter
                          setDateFilterFrom('');
                          setDateFilterTo('');
                        }
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'var(--rd-primary)',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: 'var(--rd-primary)',
                        },
                      }}
                    />
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {t('mtr.filter.customRange', 'Datumsbereich')}
                    </Typography>
                  </Box>
                  
                  {/* Datepicker nur anzeigen wenn nicht "Alle Daten" */}
                  {!(dateFilterFrom === '' && dateFilterTo === '') && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <CustomDatePicker
                        label={t('mtr.filter.dateFrom', 'Von')}
                        value={dateFilterFrom}
                        onChange={(e) => setDateFilterFrom(e.target.value)}
                        sx={{ flex: 1 }}
                      />
                      <CustomDatePicker
                        label={t('mtr.filter.dateTo', 'Bis')}
                        value={dateFilterTo}
                        onChange={(e) => setDateFilterTo(e.target.value)}
                        sx={{ flex: 1 }}
                      />
                    </Box>
                  )}
                </Box>
                
                {/* Rechte Spalte: Alle drei Dropdown-Filter FEST positioniert */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControl size="small">
                    <InputLabel>{t('mtr.filter.modality', 'Modalität')}</InputLabel>
                    <Select
                      value={filters.modality}
                      label={t('mtr.filter.modality', 'Modalität')}
                      onChange={(e) => handleFilterChange('modality', e.target.value)}
                    >
                      <MenuItem value="">{t('common.all', 'Alle')}</MenuItem>
                      {availableModalities.map((modality) => (
                        <MenuItem key={modality} value={modality}>
                          {modality}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small">
                    <InputLabel>{t('mtr.filter.status', 'Status')}</InputLabel>
                    <Select
                      value={filters.status}
                      label={t('mtr.filter.status', 'Status')}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="all">{t('common.all', 'Alle')}</MenuItem>
                      <MenuItem value="NEW">{t('status.new', 'Neu')}</MenuItem>
                      <MenuItem value="APPROVED">{t('status.approved', 'Freigegeben')}</MenuItem>
                      <MenuItem value="REJECTED">{t('status.rejected', 'Abgelehnt')}</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl size="small">
                    <InputLabel>{t('mtr.filter.referring_physician', 'Zuweiser')}</InputLabel>
                    <Select
                      value={filters.referring_physician}
                      label={t('mtr.filter.referring_physician', 'Zuweiser')}
                      onChange={(e) => handleFilterChange('referring_physician', e.target.value)}
                    >
                      <MenuItem value="">{t('common.all', 'Alle')}</MenuItem>
                      {availableReferringPhysicians.map((physician) => (
                        <MenuItem key={physician} value={physician}>
                          {physician}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              </Box>
            </Box>

            {/* 2. Spalte - Patientenliste */}
            <Box sx={{ 
              flex: 1,
              borderRight: `1px solid ${'var(--rd-divider)'}`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              backgroundColor: 'var(--rd-bg-default)',
              minWidth: 0
            }}>
              <Box sx={{ 
                p: 0.5, 
                borderBottom: `1px solid ${'var(--rd-divider)'}`,
                backgroundColor: 'var(--rd-bg-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexShrink: 0
              }}>
                <Typography variant="caption" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold', fontSize: '0.75rem', flexShrink: 0 }}>
                  {t('mtr.patients', 'Patienten')} ({filteredExams.length})
                </Typography>
                {/* Kleines Suchfeld in Überschrift */}
                <TextField
                  size="small"
                  placeholder={t('mtr.search.placeholder', 'Suche')}
                  value={filters.patient}
                  onChange={(e) => handleFilterChange('patient', e.target.value)}
                  sx={{ 
                    width: '150px',
                    m: 0,
                    '& .MuiOutlinedInput-root': {
                      height: '20px',
                      fontSize: '0.65rem'
                    },
                    '& .MuiOutlinedInput-input': {
                      py: 0,
                      px: 0.5
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlined sx={{ fontSize: '14px' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              
              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto',
                overflowX: 'hidden',
                // SCROLLBAR UNSICHTBAR ABER FUNKTIONAL!
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none'
                }
              }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <List dense>
                    {filteredExams.map((exam) => (
                      <Box key={exam.id}>
                        <ListItem
                          selected={selectedExam?.id === exam.id}
                          onClick={() => handleExamSelect(exam)}
                          sx={{ 
                            borderBottom: `1px solid ${'var(--rd-divider)'}`,
                            py: 0.5,
                            px: 2, // Mehr Padding links und rechts
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            // Explizite Selektion basierend auf State
                            backgroundColor: selectedExam?.id === exam.id 
                              ? (mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 20%, transparent) !important' : 'color-mix(in srgb, var(--rd-primary) 15%, transparent) !important')
                              : 'transparent',
                            borderLeft: selectedExam?.id === exam.id ? `3px solid var(--rd-primary)` : 'none',
                            '&:hover': {
                              backgroundColor: selectedExam?.id === exam.id
                                ? (mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 25%, transparent) !important' : 'color-mix(in srgb, var(--rd-primary) 18%, transparent) !important')
                                : (mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 10%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'),
                              transform: 'translateX(2px)'
                            },
                            '&.Mui-selected': {
                              backgroundColor: mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 20%, transparent) !important' : 'color-mix(in srgb, var(--rd-primary) 15%, transparent) !important',
                              borderLeft: `3px solid var(--rd-primary)`
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 25%, transparent) !important' : 'color-mix(in srgb, var(--rd-primary) 18%, transparent) !important'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1.5 }}>
                            {/* Links: Status + Patientenname *Geburtsdatum */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: '0 0 48%', minWidth: 0 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  backgroundColor: getStatusColor(exam.status),
                                  mr: 1,
                                  flexShrink: 0,
                                  mt: '1px'
                                }}
                              />
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', mb: 0.25 }}>
                                  {exam.patient_name || t('common.unknown', 'Unbekannt')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', display: 'block', lineHeight: 1.1 }}>
                                  *{formatDateWithAge(exam.birth_date)}
                                </Typography>
                              </Box>
                            </Box>
                            
                            {/* Rechts: Studienbezeichnung und Zuweiser in 2 Reihen */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8rem', display: 'block', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.25 }}>
                                {exam.study_description || t('mtr.noDescription', 'Keine Beschreibung')}
                              </Typography>
                              {exam.referring_physician && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', display: 'block', lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {exam.referring_physician}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </ListItem>
                        
                        {/* Erweiterte Details: Kommentar und Untersuchungsprotokoll */}
                        <Collapse in={selectedExam?.id === exam.id}>
                          <Box sx={{ 
                            px: 2, 
                            py: 1, 
                            backgroundColor: mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 8%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 5%, transparent)',
                            borderBottom: `1px solid ${'var(--rd-divider)'}`,
                            borderLeft: `3px solid var(--rd-primary)`
                          }}>
                            {exam.comment && (
                              <Box sx={{ mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem', color: 'var(--rd-primary)', display: 'block' }}>
                                  {t('mtr.comment', 'Kommentar')}:
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', whiteSpace: 'pre-wrap' }}>
                                  {exam.comment}
                                </Typography>
                              </Box>
                            )}
                            {exam.scan_protocol && (
                              <Box>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem', color: 'var(--rd-primary)', display: 'block' }}>
                                  {t('mtr.scanProtocol', 'Untersuchungsprotokoll')}:
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', whiteSpace: 'pre-wrap' }}>
                                  {exam.scan_protocol}
                                </Typography>
                              </Box>
                            )}
                            {!exam.comment && !exam.scan_protocol && (
                              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic' }}>
                                {t('mtr.noAdditionalInfo', 'Keine zusätzlichen Informationen')}
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </List>
                )}
              </Box>
            </Box>

            {/* 3. Spalte - Voruntersuchungen */}
            <Box sx={{ 
              flex: 1,
              borderRight: `1px solid ${'var(--rd-divider)'}`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              backgroundColor: 'var(--rd-bg-default)',
              minWidth: 0
            }}>
              <Box sx={{ 
                p: 0.5, 
                borderBottom: `1px solid ${'var(--rd-divider)'}`,
                backgroundColor: 'var(--rd-bg-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
              }}>
                <Typography variant="caption" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold', fontSize: '0.75rem', flexGrow: 1 }}>
                  {selectedExam ? 
                    `${t('mtr.priorExams', 'Voruntersuchungen')} für ${selectedExam.patient_name}` :
                    t('mtr.priorExams', 'Voruntersuchungen')
                  }
                </Typography>
                {availablePriorModalities.length > 0 && (
                  <Select
                    size="small"
                    value={priorExamsModalityFilter}
                    onChange={(e) => setPriorExamsModalityFilter(e.target.value)}
                    sx={{ 
                      minWidth: 80,
                      maxWidth: 120,
                      height: 24,
                      ml: 1,
                      '& .MuiSelect-select': { 
                        py: 0.5,
                        fontSize: '0.7rem',
                        color: 'var(--rd-text-primary)'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--rd-border)'
                      }
                    }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '0.7rem' }}>{t('common.all', 'Alle')}</MenuItem>
                    {availablePriorModalities.map((modality) => (
                      <MenuItem key={modality} value={modality} sx={{ fontSize: '0.7rem' }}>
                        {modality}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Box>
              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto',
                overflowX: 'hidden',
                // SCROLLBAR UNSICHTBAR ABER FUNKTIONAL!
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none'
                }
              }}>
                {selectedExam ? (
                  loadingPrevious ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <List dense>
                      {filteredPreviousExams.length === 0 ? (
                        <ListItem>
                          <ListItemText 
                            primary={previousExams.length === 0 ? t('mtr.noExams', 'Keine Voruntersuchungen gefunden') : t('mtr.noExamsForModality', 'Keine Voruntersuchungen für diese Modalität')}
                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          />
                        </ListItem>
                      ) : (
                        filteredPreviousExams.map((study, index) => (
                        <ListItem
                          key={index}
                          onDoubleClick={() => handleStudyOpen(study)}
                          sx={{ 
                            borderBottom: `1px solid ${'var(--rd-divider)'}`,
                            py: 0.5,
                            px: 2, // Konsistente Padding wie bei Patienten
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 10%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 8%, transparent)',
                              transform: 'translateX(2px)',
                              borderLeft: `2px solid var(--rd-primary)`
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 1.5 }}>
                            {/* Links: Studienbeschreibung */}
                            <Box sx={{ flex: '0 0 50%', minWidth: 0 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', mb: 0.25 }}>
                                {study.study_description || t('mtr.noDescription', 'Keine Beschreibung')}
                              </Typography>
                            </Box>
                            
                            {/* Rechts: Details in 2 Zeilen */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block', lineHeight: 1.2, mb: 0.25 }}>
                                {study.study_date || 'N/A'} • {study.modality || 'N/A'}
                              </Typography>
                              {study.accession_number && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block', lineHeight: 1.1 }}>
                                  ACC: {study.accession_number}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          </ListItem>
                        ))
                      )}
                      <Box sx={{ 
                        p: 1, 
                        bgcolor: 'var(--rd-bg-subtle)', 
                        borderRadius: 1, 
                        mt: 1 
                      }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                          {t('mtr.doubleClickToOpen', 'Doppelklick zum Öffnen der Studie')}
                        </Typography>
                      </Box>
                    </List>
                  )
                ) : (
                  <Box sx={{ p: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('mtr.selectExamFromList', 'Wählen Sie eine Untersuchung aus der Liste aus.')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* 4. Spalte - Chat (flexibel) */}
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              flexShrink: 0
            }}>
              <MTRChatMini orientation="landscape" />
            </Box>
          </>
        ) : (
          // Hochformat: Neue Aufteilung ohne Scrollbars
          <>
            {/* 1. Filter Button + Suchzeile (kompakt) */}
            <Box sx={{ 
              backgroundColor: 'var(--rd-bg-subtle)',
              borderBottom: `1px solid ${'var(--rd-divider)'}`,
              p: 0.5, // REDUZIERTES PADDING
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5, // REDUZIERTES GAP
              flexShrink: 0, // Nicht schrumpfen lassen
              position: 'relative',
              zIndex: 10, // Über anderen Inhalten
              boxSizing: 'border-box',
              minHeight: 'auto',
              maxHeight: 'none'
            }}>
              {/* Filter Button + Aktualisieren Button */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {/* Filter Button */}
                <Button
                  fullWidth
                  size="small"
                  variant={hasActiveFilters ? 'contained' : 'outlined'}
                  onClick={() => setFilterDialogOpen(!filterDialogOpen)}
                  startIcon={<FilterListOutlined />}
                  sx={{
                    backgroundColor: hasActiveFilters ? 'var(--rd-primary)' : 'transparent',
                    borderColor: 'var(--rd-primary)',
                    color: hasActiveFilters ? 'var(--rd-primary-contrast)' : 'var(--rd-primary)',
                    '&:hover': {
                      backgroundColor: hasActiveFilters ? 'var(--rd-primary-hover)' : 'color-mix(in srgb, var(--rd-primary) 10%, transparent)',
                      borderColor: 'var(--rd-primary-hover)'
                    }
                  }}
                >
                  {t('mtr.filter.title', 'Filter')}{hasActiveFilters ? ` (${t('mtr.filter.active', 'aktiv')})` : ''}
                </Button>
                
                {/* Aktualisieren Button - klein, quadratisch */}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={fetchExams}
                  sx={{
                    minWidth: '32px',
                    width: '32px',
                    height: '32px',
                    p: 0,
                    borderColor: 'var(--rd-primary)',
                    color: 'var(--rd-primary)',
                    '&:hover': {
                      backgroundColor: 'color-mix(in srgb, var(--rd-primary) 10%, transparent)',
                      borderColor: 'var(--rd-primary-hover)'
                    }
                  }}
                >
                  <ReplayIcon sx={{ fontSize: '1rem' }} />
                </Button>
              </Box>
              
              {/* Filter Overlay */}
              <Collapse in={filterDialogOpen}>
                <Paper
                  elevation={8}
                  sx={{
                    position: 'relative',
                    zIndex: 1000,
                    p: 2,
                    mt: 0.5,
                    backgroundColor: 'var(--rd-bg-paper)',
                    border: `1px solid ${'var(--rd-border)'}`
                  }}
                >
                  {/* Portrait: Einspaltige Filter-Darstellung */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Datumsbereich Toggle Switch */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {t('mtr.filter.allDates', 'Alle Daten')}
                      </Typography>
                      <Switch
                        size="small"
                        checked={!(dateFilterFrom === '' && dateFilterTo === '')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Aktiviere Datumsfilter
                            const today = new Date().toISOString().split('T')[0];
                            setDateFilterFrom(today);
                            setDateFilterTo(today);
                          } else {
                            // Deaktiviere Datumsfilter
                            setDateFilterFrom('');
                            setDateFilterTo('');
                          }
                        }}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'var(--rd-primary)',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: 'var(--rd-primary)',
                          },
                        }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {t('mtr.filter.customRange', 'Datumsbereich')}
                      </Typography>
                    </Box>
                    
                    {/* Datepicker nur anzeigen wenn nicht "Alle Daten" */}
                    {!(dateFilterFrom === '' && dateFilterTo === '') && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <CustomDatePicker
                          label={t('mtr.filter.dateFrom', 'Von')}
                          value={dateFilterFrom}
                          onChange={(e) => setDateFilterFrom(e.target.value)}
                          sx={{ flex: 1 }}
                        />
                        <CustomDatePicker
                          label={t('mtr.filter.dateTo', 'Bis')}
                          value={dateFilterTo}
                          onChange={(e) => setDateFilterTo(e.target.value)}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    )}
                    
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('mtr.filter.modality', 'Modalität')}</InputLabel>
                      <Select
                        value={filters.modality}
                        label={t('mtr.filter.modality', 'Modalität')}
                        onChange={(e) => handleFilterChange('modality', e.target.value)}
                      >
                        <MenuItem value="">{t('common.all', 'Alle')}</MenuItem>
                        {availableModalities.map((modality) => (
                          <MenuItem key={modality} value={modality}>
                            {modality}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    
                    <FormControl fullWidth size="small">
                      <InputLabel>{t('mtr.filter.status', 'Status')}</InputLabel>
                      <Select
                        value={filters.status}
                        label={t('mtr.filter.status', 'Status')}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                      >
                        <MenuItem value="all">{t('common.all', 'Alle')}</MenuItem>
                        <MenuItem value="NEW">{t('status.new', 'Neu')}</MenuItem>
                        <MenuItem value="APPROVED">{t('status.approved', 'Freigegeben')}</MenuItem>
                        <MenuItem value="REJECTED">{t('status.rejected', 'Abgelehnt')}</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                      <InputLabel>{t('mtr.filter.referring_physician', 'Zuweiser')}</InputLabel>
                      <Select
                        value={filters.referring_physician}
                        label={t('mtr.filter.referring_physician', 'Zuweiser')}
                        onChange={(e) => handleFilterChange('referring_physician', e.target.value)}
                      >
                        <MenuItem value="">{t('common.all', 'Alle')}</MenuItem>
                        {availableReferringPhysicians.map((physician) => (
                          <MenuItem key={physician} value={physician}>
                            {physician}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Paper>
              </Collapse>
              
              {/* Suchzeile für Patient UND Studie */}
              <TextField
                fullWidth
                size="small"
                placeholder={t('mtr.search.placeholder', 'Suche')}
                value={filters.patient}
                onChange={(e) => handleFilterChange('patient', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* 2-4: Content Area mit flexiblem Layout */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
              minWidth: 0,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}>
              {/* 2-3: Patienten + Voruntersuchungen (flexen zusammen) */}
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0
              }}>
                {/* 2. Patienten (1/2 des verfügbaren Raums) - SCROLLBAR-KILLER MODUS */}
              <Box sx={{ 
                flex: '1 1 0px', // EXAKTE FLEX BASIS!
                borderBottom: `1px solid ${'var(--rd-divider)'}`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
                minWidth: 0,
                maxWidth: '100%',
                maxHeight: '100%',
                backgroundColor: 'var(--rd-bg-default)',
                boxSizing: 'border-box'
              }}>
                <Box sx={{ 
                  p: 0.5, // GLEICHE HÖHE WIE CHAT HEADER
                  flexShrink: 0, // Header nicht schrumpfen lassen
                  backgroundColor: 'var(--rd-bg-subtle)',
                  borderBottom: `1px solid ${'var(--rd-divider)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  boxSizing: 'border-box'
                }}>
                  <Typography variant="caption" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold', fontSize: '0.75rem' }}>
                    {t('mtr.patients', 'Patienten')} ({filteredExams.length})
                  </Typography>
                </Box>
                
                <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, position: 'relative' }}>
                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : (
                    <Box sx={{ 
                      height: '100%', 
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      maxHeight: '100%',
                      boxSizing: 'border-box',
                      // SCROLLBAR UNSICHTBAR ABER SCROLLBAR FUNKTIONAL!
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      '&::-webkit-scrollbar': {
                        display: 'none'
                      }
                    }}>
                      <List dense sx={{ 
                        height: 'auto',
                        padding: 0,
                        margin: 0,
                        boxSizing: 'border-box'
                      }}>
                        {filteredExams.map((exam) => (
                          <Box key={exam.id}>
                            <ListItem
                              selected={selectedExam?.id === exam.id}
                              onClick={() => handleExamSelect(exam)}
                              sx={{ 
                                borderBottom: `1px solid ${'var(--rd-divider)'}`,
                                py: 0.5,
                                px: 1,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                // Explizite Selektion basierend auf State
                                backgroundColor: selectedExam?.id === exam.id 
                                  ? (mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 20%, transparent) !important' : 'color-mix(in srgb, var(--rd-primary) 15%, transparent) !important')
                                  : 'transparent',
                                borderLeft: selectedExam?.id === exam.id ? `3px solid var(--rd-primary)` : 'none',
                                '&:hover': {
                                  backgroundColor: selectedExam?.id === exam.id
                                    ? (mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 25%, transparent) !important' : 'color-mix(in srgb, var(--rd-primary) 18%, transparent) !important')
                                    : (mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 10%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 8%, transparent)'),
                                  transform: 'translateX(2px)'
                                },
                                '&.Mui-selected': {
                                  backgroundColor: mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 20%, transparent) !important' : 'color-mix(in srgb, var(--rd-primary) 15%, transparent) !important',
                                  borderLeft: `3px solid var(--rd-primary)`
                                },
                                '&.Mui-selected:hover': {
                                  backgroundColor: mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 25%, transparent) !important' : 'color-mix(in srgb, var(--rd-primary) 18%, transparent) !important'
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: getStatusColor(exam.status),
                                  mr: 1,
                                  flexShrink: 0
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {exam.patient_name || t('common.unknown', 'Unbekannt')}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                  {formatDateWithAge(exam.birth_date)}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block' }}>
                                  {exam.study_description || 'N/A'}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', display: 'block' }}>
                                  {t('mtr.referringPhysician', 'Zuweiser')}: {exam.referring_physician || 'N/A'}
                                </Typography>
                              </Box>
                            </ListItem>
                            
                            {/* Erweiterte Details: Kommentar und Untersuchungsprotokoll */}
                            <Collapse in={selectedExam?.id === exam.id}>
                              <Box sx={{ 
                                px: 1, 
                                py: 0.5, 
                                backgroundColor: mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 8%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 5%, transparent)',
                                borderBottom: `1px solid ${'var(--rd-divider)'}`,
                                borderLeft: `3px solid var(--rd-primary)`
                              }}>
                                {exam.comment && (
                                  <Box sx={{ mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.65rem', color: 'var(--rd-primary)', display: 'block' }}>
                                      {t('mtr.comment', 'Kommentar')}:
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', whiteSpace: 'pre-wrap' }}>
                                      {exam.comment}
                                    </Typography>
                                  </Box>
                                )}
                                {exam.scan_protocol && (
                                  <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.65rem', color: 'var(--rd-primary)', display: 'block' }}>
                                      {t('mtr.scanProtocol', 'Untersuchungsprotokoll')}:
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', display: 'block', whiteSpace: 'pre-wrap' }}>
                                      {exam.scan_protocol}
                                    </Typography>
                                  </Box>
                                )}
                                {!exam.comment && !exam.scan_protocol && (
                                  <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic' }}>
                                    {t('mtr.noAdditionalInfo', 'Keine zusätzlichen Informationen')}
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </Box>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* 3. Voruntersuchungen (1/3) - SCROLLBAR-KILLER MODUS */}
              <Box sx={{ 
                flex: '1 1 0px', // EXAKTE FLEX BASIS!
                borderBottom: `1px solid ${'var(--rd-divider)'}`,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
                minWidth: 0,
                maxWidth: '100%',
                maxHeight: '100%',
                backgroundColor: 'var(--rd-bg-default)',
                boxSizing: 'border-box'
              }}>
                <Box sx={{ 
                  p: 0.5, // GLEICHE HÖHE WIE CHAT HEADER
                  flexShrink: 0, // Header nicht schrumpfen lassen
                  backgroundColor: 'var(--rd-bg-subtle)',
                  borderBottom: `1px solid ${'var(--rd-divider)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  boxSizing: 'border-box'
                }}>
                  <Typography variant="caption" sx={{ color: 'var(--rd-primary)', fontWeight: 'bold', fontSize: '0.75rem', flexGrow: 1 }}>
                    {t('mtr.priorExams', 'Voruntersuchungen')}
                  </Typography>
                  {availablePriorModalities.length > 0 && (
                    <Select
                      size="small"
                      value={priorExamsModalityFilter}
                      onChange={(e) => setPriorExamsModalityFilter(e.target.value)}
                      sx={{ 
                        minWidth: 60,
                        maxWidth: 100,
                        height: 20,
                        ml: 0.5,
                        '& .MuiSelect-select': { 
                          py: 0.25,
                          fontSize: '0.6rem',
                          color: 'var(--rd-text-primary)'
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'var(--rd-border)'
                        }
                      }}
                    >
                      <MenuItem value="all" sx={{ fontSize: '0.6rem' }}>{t('common.all', 'Alle')}</MenuItem>
                      {availablePriorModalities.map((modality) => (
                        <MenuItem key={modality} value={modality} sx={{ fontSize: '0.6rem' }}>
                          {modality}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Box>
                
                <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0, position: 'relative' }}>
                  {selectedExam ? (
                    loadingPrevious ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={20} />
                      </Box>
                    ) : (
                      <Box sx={{ 
                        height: '100%', 
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        maxHeight: '100%',
                        boxSizing: 'border-box',
                        // SCROLLBAR UNSICHTBAR ABER SCROLLBAR FUNKTIONAL!
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                        '&::-webkit-scrollbar': {
                          display: 'none'
                        }
                      }}>
                        <List dense sx={{ 
                          height: 'auto',
                          padding: 0,
                          margin: 0,
                          boxSizing: 'border-box'
                        }}>
                          {filteredPreviousExams.length === 0 ? (
                            <ListItem sx={{ py: 0.5 }}>
                              <ListItemText 
                                primary={previousExams.length === 0 ? 
                                  t('mtr.noExams', 'Keine Voruntersuchungen') : 
                                  t('mtr.noExamsForModality', 'Keine Voruntersuchungen für diese Modalität')
                                }
                                primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                              />
                            </ListItem>
                          ) : (
                            filteredPreviousExams.map((study, index) => (
                              <ListItem
                                key={index}
                                onDoubleClick={() => handleStudyOpen(study)}
                                sx={{ 
                                  borderBottom: `1px solid ${'var(--rd-divider)'}`,
                                  py: 0.5,
                                  px: 1,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: mode === 'dark' ? 'color-mix(in srgb, var(--rd-primary) 10%, transparent)' : 'color-mix(in srgb, var(--rd-primary) 8%, transparent)',
                                    transform: 'translateX(2px)',
                                    borderLeft: `2px solid var(--rd-primary)`
                                  }
                                }}
                              >
                                <ListItemText 
                                  primary={study.study_description || t('mtr.noDescription', 'Keine Beschreibung')}
                                  secondary={`${study.study_date || 'N/A'} • ${study.modality || 'N/A'}`}
                                  primaryTypographyProps={{ variant: 'caption', fontWeight: 'bold' }}
                                  secondaryTypographyProps={{ variant: 'caption', fontSize: '0.65rem' }}
                                />
                              </ListItem>
                            ))
                          )}
                        </List>
                      </Box>
                    )
                  ) : (
                    <Box sx={{ 
                      p: 0.25, // MINIMAL PADDING
                      boxSizing: 'border-box'
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('mtr.selectPatient', 'Patient auswählen')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
              </Box>

              {/* 4. Chat (flexibel) */}
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxSizing: 'border-box',
                flexShrink: 0
              }}>
                <MTRChatMini orientation="portrait" />
              </Box>
            </Box>
          </>
        )}
      </Box>

    </Box>
  );
};

export default MTRPopup;
